import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {RegistrationStructuredTableAdapter} from 'notebook/integrations/registry/RegistrationStructuredTableAdapter';
import {
  RegistrationStructuredTableMetadata,
  RegistrationStructuredTableRowMetadata,
} from 'notebook/integrations/registry/RegistrationTableConstants';
import RegistrationTableSettingsModal from 'notebook/registration-tables/RegistrationTableSettingsModal';
import {performColumnOpAndSetLabelingStrategy} from 'notebook/registration-tables/RegistrationTableUtil';
import {applyRowByRowSubmissionSettingsUpdate} from 'notebook/structured-tables/action-functions/updateStructuredTableSettingsActionFunction';
import {StructuredTableFolderOption} from 'notebook/structured-tables/components/useSelectFolderOptionForTable';
import updateStructuredTableMetadata from 'notebook/structured-tables/util/updateStructuredTableMetadata';
import legacyRunModal from 'ui-library/legacyRunModal';

import {
  getRegistrationTableSettingsModalPropsForMonomerMapping,
  updateBasesColumnMonomerMappingConfig,
} from '../bases-columns/BasesColumnConstants';
import {ToolbarButtonActionFunction} from '../StructuredTableConstants';
import makeSimpleActionFunction from './makeSimpleActionFunction';

/**
 * An ActionFunction that lets the user view or update the table settings. Can only be changed if the editor
 * is unlocked and the table hasn't been submitted.
 */
export const updateRegistrationTableSettingsActionFunction: ToolbarButtonActionFunction<
  RegistrationStructuredTableMetadata,
  RegistrationStructuredTableRowMetadata
> = makeSimpleActionFunction({
  label: 'Settings',
  icon: 'settings',
  onInvoke: async ({
    structuredTableAdapter,
    structuredTable,
    locked,
    performTableOpOnTable,
    entryOrTemplateId,
  }) => {
    const tagSchemaId = (structuredTableAdapter as RegistrationStructuredTableAdapter).getTagSchema(
      structuredTable
    )?.id;

    assert(tagSchemaId != null, 'Settings button clicked with no tag schema available');

    const registrationTableSettingsValues = await legacyRunModal(RegistrationTableSettingsModal, {
      labelingStrategy: structuredTable.metadata.labelingStrategy,
      insertType: structuredTable.metadata.insertType,
      folderConfig: {
        folderOption:
          structuredTable.metadata.folderOption ??
          StructuredTableFolderOption.DERIVE_FROM_PARENT_DOCUMENT_TYPE,
        folderId: structuredTable.metadata.folderId,
      },
      locked:
        locked ||
        /*
         * Only allow changing the labeling strategy if the editor is unlocked and we
         * don't have any registered rows.
         */
        structuredTable.rows.some((row) => row.isFinalized),
      tagSchemaId,
      ...getRegistrationTableSettingsModalPropsForMonomerMapping(structuredTable.columns),
      rowOnlyConfig: {
        showSubmissionSetting:
          BenchlingConfig.ELN_ENABLE_ROW_ONLY_TABLE_SUBMISSION && structuredTable.isConfig,
        rowOnlyInitialValue: structuredTable.metadata.rowByRowSubmissionRequired ?? false,
      },
    });
    if (registrationTableSettingsValues == null) {
      return;
    }
    const table = structuredTableAdapter.toTable(structuredTable);

    const {
      labelingStrategy: newLabelingStrategy,
      monomerMapping: newMonomerMapping,
      folderConfig: newFolderConfig,
      rowByRowSubmissionRequired: newRowByRowSubmissionRequired,
    } = registrationTableSettingsValues;

    if (newLabelingStrategy != null) {
      performColumnOpAndSetLabelingStrategy({
        table,
        performTableOpOnTable,
        labelingStrategy: newLabelingStrategy,
        isStructuredTable: true,
      });
    }
    if (newMonomerMapping != null) {
      updateBasesColumnMonomerMappingConfig({
        performTableOpOnTable,
        monomerMapping: newMonomerMapping,
      });
    }
    if (newFolderConfig != null) {
      updateStructuredTableMetadata<RegistrationStructuredTableMetadata>({
        performTableOpOnTable,
        metadata: newFolderConfig,
      });
    }
    applyRowByRowSubmissionSettingsUpdate({
      performTableOpOnTable,
      values: {rowByRowSubmissionRequired: newRowByRowSubmissionRequired},
      analyticsParams: {structuredTable, entryOrTemplateId},
    });
  },
  getTooltip: () => 'Settings',
  getDisabled: ({structuredTableAdapter, structuredTable, isProtected}) => {
    const tagSchema = (structuredTableAdapter as RegistrationStructuredTableAdapter).getTagSchema(
      structuredTable
    );
    const hasNoTagSchema = tagSchema == null;
    return hasNoTagSchema || Boolean(isProtected);
  },
  getHidden: () => false,
});
